import React, { FC } from 'react'
import { InView } from 'react-intersection-observer'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import { LandscapeTileCard } from '@/molecules/LandscapeRowCollection'
import {
  DiscoverItemInteraction,
  IMPRESSION_VISIBILITY_THRESHOLD,
  useCardInteractions,
} from '@/services/Analytics/ContentInteractions'
import { ContentSneakPeek } from '@/services/ProjectsService'
import { LinkViewModel } from '@/services/RenderService'
import { useSafeTrack } from '@/utils/analytics'
import { TranslateFunction } from '@/utils/translate/translate-client'

export interface SneakPeekCardProps {
  item: ContentSneakPeek
  linkModel: LinkViewModel
  itemIndex: number
  hoverStartTimeRef: React.MutableRefObject<Map<string, number>>
  impressionTimeoutRef: React.MutableRefObject<Map<string, NodeJS.Timeout>>
  hasTrackedImpression: React.MutableRefObject<Set<string>>
  createInteraction: (item: ContentSneakPeek, itemIndex: number) => DiscoverItemInteraction | null
  t: TranslateFunction
}

export const SneakPeekCard: FC<SneakPeekCardProps> = ({
  item,
  linkModel,
  itemIndex,
  hoverStartTimeRef,
  impressionTimeoutRef,
  hasTrackedImpression,
  createInteraction,
  t,
}) => {
  const track = useSafeTrack()
  const contentId = item?.id ?? ''

  const { handleMouseEnter, handleMouseLeave, handleClick, handleInViewChange } = useCardInteractions({
    contentId,
    item,
    itemIndex,
    hoverStartTimeRef,
    impressionTimeoutRef,
    hasTrackedImpression,
    createInteraction,
    onClickTrack: () => track('Sneak Peek Rail Item Clicked'),
  })

  return (
    <LazyHydrate key={item?.slug} whenVisible>
      <>
        <InView onChange={handleInViewChange} triggerOnce={false} threshold={IMPRESSION_VISIBILITY_THRESHOLD}>
          <LandscapeTileCard
            className="mb-2 mr-7"
            linkModel={linkModel}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <PlayButtonThumbnailOverlay />
          </LandscapeTileCard>
        </InView>
        <TitleAndSubtitleDisplay
          className="mr-7"
          title={t('sneakPeek', 'Sneak Peek')}
          subtitle={item?.project?.name}
          duration={item.duration}
        />
      </>
    </LazyHydrate>
  )
}
